import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  Chip,
  CssBaseline,
  Drawer,
  ListSubheader,
  Stack,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Menu,
  Logout,
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { buildBreadcrumbs, getRouteMeta, sidebarSections } from '../config/adminNavigation';
import { hasRequiredRole } from '../config/permissions';

const drawerWidth = 240;
const mobileDrawerWidth = 320;

const MainLayout = () => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const routeMeta = getRouteMeta(location.pathname);
  const breadcrumbs = useMemo(
    () => buildBreadcrumbs(location.pathname),
    [location.pathname]
  );
  const visibleSections = sidebarSections.map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      hasRequiredRole(user?.role, item.allowedRoles)
    ),
  }));

  const drawer = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background:
          'linear-gradient(180deg, rgba(15,118,110,0.96) 0%, rgba(17,94,89,1) 100%)',
        color: 'white',
      }}
    >
      <Box sx={{ px: 3, py: 3 }}>
        <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: '0.18em' }}>
          Admin Workspace
        </Typography>
        <Typography variant="h5" sx={{ mt: 1 }}>
          Circle of Humanity
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.78, mt: 1 }}>
          Control causes, users, reporting, and operational settings from one place.
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2 }}>
        {visibleSections.map((section) =>
          section.items.length > 0 ? (
            <List
              key={section.label}
              subheader={
                <ListSubheader
                  disableGutters
                  sx={{
                    bgcolor: 'transparent',
                    color: alpha('#ffffff', 0.72),
                    px: 2,
                    py: 1,
                    lineHeight: 1.2,
                    fontSize: 12,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                  }}
                >
                  {section.label}
                </ListSubheader>
              }
            >
              {section.items.map((item) => {
                const Icon = item.icon!;
                const selected = item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);

                return (
                  <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={selected}
                      onClick={() => {
                        navigate(item.path);
                        setMobileOpen(false);
                      }}
                      sx={{
                        borderRadius: 3,
                        mx: 1,
                        '&.Mui-selected': {
                          bgcolor: alpha('#ffffff', 0.16),
                          '&:hover': {
                            bgcolor: alpha('#ffffff', 0.22),
                          },
                        },
                        '&:hover': {
                          bgcolor: alpha('#ffffff', 0.08),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                        <Icon />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.navLabel}
                        secondary={item.description}
                        secondaryTypographyProps={{
                          sx: { color: alpha('#ffffff', 0.66), mt: 0.25 },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          ) : null
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
          borderBottom: (muiTheme) => `1px solid ${muiTheme.palette.divider}`,
          backgroundColor: alpha('#ffffff', 0.88),
          backdropFilter: 'blur(12px)',
        }}
      >
        <Toolbar>
          {!isDesktop ? (
            <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ mr: 1 }}>
              <Menu />
            </IconButton>
          ) : null}
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {routeMeta?.title || 'Admin'}
            </Typography>
            <Typography variant="h6" noWrap component="div">
              {routeMeta?.description || 'Administrative control center'}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Chip
              label={user?.role || 'Unknown role'}
              color="primary"
              variant="outlined"
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                {user?.email?.[0]?.toUpperCase() || 'A'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body2" fontWeight={600}>
                  {user?.email || 'Admin'}
                </Typography>
              </Box>
            </Stack>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: isDesktop ? drawerWidth : mobileDrawerWidth,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: isDesktop ? drawerWidth : mobileDrawerWidth,
            boxSizing: 'border-box',
            borderRight: 'none',
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 2, md: 4 },
          py: 3,
        }}
      >
        <Toolbar />
        <Stack spacing={3}>
          <Breadcrumbs aria-label="breadcrumb">
            {breadcrumbs.map((crumb, index) =>
              crumb.path && index < breadcrumbs.length - 1 ? (
                <Typography
                  key={`${crumb.label}-${index}`}
                  component={RouterLink}
                  to={crumb.path}
                  color="inherit"
                  sx={{ textDecoration: 'none' }}
                >
                  {crumb.label}
                </Typography>
              ) : (
                <Typography key={`${crumb.label}-${index}`} color="text.primary">
                  {crumb.label}
                </Typography>
              )
            )}
          </Breadcrumbs>
          <Outlet />
        </Stack>
      </Box>
    </Box>
  );
};

export default MainLayout;
